import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { supabase } from '../lib/supabase';
import { useIsAdmin } from './AdminDashboard/useIsAdmin';

/**
 * OAuth consent screen for the Billboard MCP server (server/mcp/oauth.js).
 *
 * Supabase's OAuth server sends the browser here (its configured authorization
 * path) with ?authorization_id=. AuthGate has already made the user sign in.
 * We show which app is asking and approve/deny via supabase.auth.oauth, which
 * redirects back to the app. The MCP itself is admin-only, so non-admins can
 * only deny (an approved token would be refused anyway).
 */
type Details = { clientName: string; scopes: string[]; email: string };

const OAuthConsent: React.FC = () => {
  const [params] = useSearchParams();
  const authorizationId = params.get('authorization_id') || '';
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [details, setDetails] = useState<Details | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authorizationId) {
      setError('Missing authorization request. Start the connection from Claude again.');
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      if (cancelled) return;
      if (err) {
        setError(`Couldn't load the authorization request: ${err.message}`);
        return;
      }
      // Already approved before: Supabase hands back the redirect directly.
      if (data && !('authorization_id' in data) && 'redirect_url' in data && data.redirect_url) {
        window.location.assign(data.redirect_url);
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      const d = data as { client?: { name?: string }; scope?: string } | null;
      setDetails({
        clientName: d?.client?.name || 'An application',
        scopes: (d?.scope || 'email').split(' ').filter(Boolean),
        email: session?.user?.email || '',
      });
    })();
    return () => { cancelled = true; };
  }, [authorizationId]);

  // Both calls redirect the browser back to the requesting app on success.
  const decide = async (approve: boolean) => {
    setBusy(true);
    const { error: err } = approve
      ? await supabase.auth.oauth.approveAuthorization(authorizationId)
      : await supabase.auth.oauth.denyAuthorization(authorizationId);
    if (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper sx={{ p: 4, maxWidth: 480, width: '100%' }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Connect to Billboard</Typography>
        {error ? (
          <Typography color="error">{error}</Typography>
        ) : !details || adminLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            <Typography sx={{ mb: 1 }}>
              <strong>{details.clientName}</strong> wants read-only access to Billboard data (chart weeks, videos,
              renders, billing and usage) as <strong>{details.email}</strong>.
            </Typography>
            {!isAdmin && (
              <Typography color="error" sx={{ mb: 1 }}>
                Billboard data in Claude is limited to admins, so this account can't connect.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Requested: {details.scopes.join(', ')}. It can't change anything, and every request is logged.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" disabled={busy} onClick={() => decide(false)}>Deny</Button>
              <Button variant="contained" disabled={busy || !isAdmin} onClick={() => decide(true)}>Approve</Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default OAuthConsent;
